import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, InjectionToken, Optional } from "@angular/core";
import { MiscUtils } from "@dtm-frontend/shared/utils";
import { TranslocoService } from "@jsverse/transloco";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from "@ngxs/store";
import { distinctUntilChanged } from "rxjs/operators";
import { LANGUAGE_CONFIGURATION, LanguageCode } from "../modules/i18n";
import { Theme, UIActions } from "./ui.actions";

export interface UIStateModel {
    isMenuCollapsed: boolean;
    activeLanguage: LanguageCode;
    theme: Theme | null;
    shouldShowUserInteractionOverlay: boolean;
    defaultTheme: Theme;
}

const USER_INTERACTION_OVERLAY_DELAY_MS = 5000;

export interface UIThemeConfiguration {
    defaultTheme: Theme;
    supportedThemes: Theme[];
}

export const UI_THEME_CONFIGURATION = new InjectionToken<UIThemeConfiguration>("UI_THEME_CONFIGURATION");

export const DEFAULT_UI_STATE = {
    isMenuCollapsed: false,
    activeLanguage: LANGUAGE_CONFIGURATION.defaultLanguage,
    theme: null,
    shouldShowUserInteractionOverlay: false,
    defaultTheme: Theme.Light,
};

@UntilDestroy()
@State<UIStateModel>({
    name: "ui",
    defaults: DEFAULT_UI_STATE,
})
@Injectable()
export class UIState implements NgxsOnInit {
    constructor(
        private readonly translocoService: TranslocoService,
        private readonly store: Store,
        @Inject(DOCUMENT) private readonly document: Document,
        @Optional() @Inject(UI_THEME_CONFIGURATION) private readonly themeConfig: UIThemeConfiguration | null
    ) {}

    public ngxsOnInit(context: StateContext<UIStateModel>): void {
        context.patchState({
            shouldShowUserInteractionOverlay: false,
            defaultTheme: this.getDefaultTheme(),
            activeLanguage: this.translocoService.getActiveLang() as LanguageCode,
        });

        const hasThemeSet = !!context.getState().theme;
        const theme = this.resolveToSupportedTheme(context.getState().theme, context.getState().defaultTheme);

        if (!hasThemeSet) {
            context.patchState({ theme });
        }

        this.setDocumentTheme(theme);

        setTimeout(() => {
            context.dispatch(UIActions.EnsureUserInteraction);
        }, USER_INTERACTION_OVERLAY_DELAY_MS);

        this.store
            .select(UIState.activeLanguage)
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((language) => {
                if (language) {
                    this.translocoService.setActiveLang(language);
                }
            });
        this.store
            .select(UIState.theme)
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((newTheme) => {
                this.setDocumentTheme(newTheme);
            });
    }

    @Selector()
    public static isMenuCollapsed(state: UIStateModel): boolean {
        return state.isMenuCollapsed;
    }

    @Selector()
    public static activeLanguage(state: UIStateModel): LanguageCode {
        return state.activeLanguage;
    }

    @Selector()
    public static theme(state: UIStateModel): Theme {
        return state.theme ?? state.defaultTheme;
    }

    @Selector()
    public static shouldShowUserInteractionOverlay(state: UIStateModel): boolean {
        return state.shouldShowUserInteractionOverlay;
    }

    @Action(UIActions.EnsureUserInteraction)
    public async ensureUserInteraction(context: StateContext<UIStateModel>): Promise<void> {
        const isAutoplayAllowed = await MiscUtils.checkIfAutoplayIsAllowed();

        context.patchState({ shouldShowUserInteractionOverlay: !isAutoplayAllowed });
    }

    @Action(UIActions.SetMenuCollapsedState)
    public setMenuCollapsedState(context: StateContext<UIStateModel>, action: UIActions.SetMenuCollapsedState): void {
        context.patchState({ isMenuCollapsed: action.isCollapsed });
    }

    @Action(UIActions.SetActiveLanguage)
    public setActiveLanguage(context: StateContext<UIStateModel>, action: UIActions.SetActiveLanguage): void {
        context.patchState({ activeLanguage: action.languageCode });
    }

    @Action(UIActions.SetTheme)
    public setTheme(context: StateContext<UIStateModel>, action: UIActions.SetTheme): void {
        const state = context.getState();
        const theme = this.resolveToSupportedTheme(action.theme, state.defaultTheme);
        context.patchState({ theme });
    }

    private resolveToSupportedTheme(theme: Theme | null, defaultTheme: Theme): Theme {
        const supported = this.themeConfig?.supportedThemes ?? [Theme.Light];
        if (theme && supported.includes(theme)) {
            return theme;
        }

        return supported.includes(defaultTheme) ? defaultTheme : supported[0];
    }

    private setDocumentTheme(theme: Theme): void {
        const body = this.document?.body;

        if (!body || !("dataset" in body)) {
            return;
        }

        body.dataset.theme = theme;
    }

    private getDefaultTheme(): Theme {
        return (
            this.themeConfig?.defaultTheme ??
            (this.document?.defaultView?.matchMedia?.("(prefers-color-scheme: dark)").matches ? Theme.Dark : Theme.Light)
        );
    }
}
